import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { getHours } from "date-fns";
import {
  Box,
  Divider,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import styled from "@emotion/styled";
import { useMyself } from "@api/v1/hooks/useMyself";
import { PageLayout } from "@components";
import { intervalToTimeRange } from "@helpers";
import { DepotsSelector } from "@features/selectDepots/DepotsSelector";
import { useDashboardSelectedGroupsContext } from "@features/dashboard/DashboardSelectedGroupProvider";
import { DashboardChartContainer } from "@features/dashboard/components/Dashboard/DashboardChartContainer";
import { useAppLayout } from "@providers";
import { DashboardNotificationManager } from "@features/dashboard/components/DashboardNotificationManager";
import { getWelcomePhrase } from "@features/dashboard/utils";
import { DashboardPerformance } from "@features/dashboard/components";
import { useInvestmentsCount } from "@features/dashboard/hooks";
import {
  DashboardActions,
  DashboardActionsHandle,
} from "@features/dashboard/DashboardActions";
import { DEFAULT_CURRENCY } from "@constants";
import { useInvestmentsChartDataTotal } from "../../../hooks/chart/useInvestmentsChartDataTotal";
import { Assets } from "./Assets";

const Container = styled(Box)`
  min-height: 620px;
  display: flex;
  align-items: stretch;
`;

const MetricsContainer = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme, fullWidth }) =>
    fullWidth ? theme.spacing(5) : theme.spacing(3)};
  width: ${({ fullWidth }) => (fullWidth ? "100%" : "55%")};
  flex-shrink: 0;

  ${({ fullWidth }) =>
    fullWidth &&
    `
    flex-direction: column-reverse;
    min-width: unset;
  `}

  ${({ theme }) => theme.breakpoints.down("md")} {
    flex-direction: column-reverse;
    gap: ${({ theme }) => theme.spacing(5)};
    width: 100%;
    min-width: unset;
  }

  & > :not(style) {
    flex: 1;
  }
`;

const StyledDivider = styled(Divider)`
  border-color: ${({ theme }) => theme.palette.border.secondary};
`;

export const DashboardPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isLessMd = useMediaQuery(theme.breakpoints.down("md"));
  const isLess1100 = useMediaQuery(theme.breakpoints.down(1100));
  const isXs = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const { startDate, endDate } = intervalToTimeRange("oneYear");
  const { sidebarCollapsed } = useAppLayout();
  const dashboardActionsRef = useRef<DashboardActionsHandle>(null);

  const { selectedGroup, groupedAccountIds, accounts, refetch, isFetching } =
    useDashboardSelectedGroupsContext();

  const accountIds = useMemo(
    () => [
      ...groupedAccountIds.cashIds,
      ...groupedAccountIds.creditCardIds,
      ...groupedAccountIds.depotIds,
    ],
    [groupedAccountIds]
  );

  const {
    statistic,
    currency,
    isFetching: isStatisticLoading,
  } = useInvestmentsChartDataTotal(accountIds, startDate!, endDate!);

  const investmentsCount = useInvestmentsCount(
    accounts,
    groupedAccountIds.depotIds
  );

  const { data: myselfData } = useMyself();

  useEffect(() => {
    if (!isFetching && accountIds?.length === 0)
      dashboardActionsRef.current?.openModal();
  }, [isFetching, accountIds]);

  const totalValue = statistic?.portfolioValue;
  const name = myselfData?.profileReference?.firstName;
  const welcomePhrase = getWelcomePhrase(getHours(new Date()));
  const welcomeText = name ? `${welcomePhrase}, ${name}` : welcomePhrase;

  return (
    <PageLayout
      isMainPage
      hasAddButton
      title={
        <Box>
          <Typography variant="body4" color="textSecondary">
            {welcomeText}
          </Typography>
          <Typography variant="h4" component="h1" mb={theme.spacing(1)}>
            {t("dashboardPage.header.title")}
          </Typography>
          <DepotsSelector
            selectedGroup={selectedGroup}
            selectedGroupParams={{ id: "dashboard" }}
            color="text.secondary"
            onSave={() => refetch()}
          />
        </Box>
      }
      actionButton={<DashboardActions ref={dashboardActionsRef} />}
    >
      <DashboardNotificationManager />

      <Container
        flexDirection={
          isLessMd || (isLess1100 && !sidebarCollapsed) ? "column" : "row"
        }
        gap={isLessMd || (isLess1100 && !sidebarCollapsed) ? 2.5 : 2.5}
      >
        <DashboardChartContainer
          totalValue={totalValue}
          statistic={statistic}
          currency={currency ?? DEFAULT_CURRENCY}
          isStatisticsLoading={isStatisticLoading}
          statisticsFontVariant="body2"
          fullWidth
        />
        <MetricsContainer
          fullWidth={isLessMd || (isLess1100 && !sidebarCollapsed)}
        >
          <DashboardPerformance
            centered={false}
            showCompare={false}
            showDividendsToggle={false}
            showCapitalToggle={false}
            chartHeight={isLessMd ? undefined : 455}
          />
        </MetricsContainer>
        {isLessMd && <StyledDivider />}
      </Container>

      <Assets
        investmentsCount={investmentsCount}
        currency={currency ?? DEFAULT_CURRENCY}
      />
    </PageLayout>
  );
};
